
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";

const PlanEditFolder = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState("");
  const [folders, setFolders] = useState([]);
  const [newFolderName, setNewFolderName] = useState("");
  const [selectedParentId, setSelectedParentId] = useState("");
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState("");
  const [error, setError] = useState(null);

  const project = JSON.parse(localStorage.getItem("selectedProject") || "null");
  const projectId = project ? project.id : null;

  const fetchFolders = useCallback(async () => {
    try {
      const response = await fetch(
        `/api/folders?module=plan&projectId=${projectId}&view_mode=tree`,
      );
      if (!response.ok) throw new Error("Failed to fetch folders");
      const data = await response.json();
      
      // 处理不同的API响应格式
      let foldersArray = [];
      if (Array.isArray(data)) {
        foldersArray = data;
      } else if (data.folders && Array.isArray(data.folders)) {
        foldersArray = data.folders;
      } else {
        console.warn("Unexpected API response format:", data);
        foldersArray = [];
      }
      
      setFolders(foldersArray);
    } catch (err) {
      console.error("Failed to fetch folders:", err);
      setError(err.message);
      setFolders([]);
    }
  }, [projectId]);

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    if (!role || !projectId) {
      navigate("/");
    } else {
      setUserRole(role);
      fetchFolders();
    }
  }, [navigate, projectId, fetchFolders]);

  const handleAddFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    try {
      const response = await fetch("/api/folders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newFolderName.trim(),
          module: "plan",
          projectId: projectId,
          parent_id: selectedParentId || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create folder");
      }

      setNewFolderName("");
      setSelectedParentId("");
      fetchFolders();
    } catch (err) {
      console.error("Add folder error:", err);
      setError(err.message || "Failed to create folder");
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm("Are you sure you want to delete this folder?")) return;

    try {
      const response = await fetch(`/api/folders/${folderId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete folder");
      }
      fetchFolders();
    } catch (err) {
      console.error("Delete folder error:", err);
      setError(err.message || "Failed to delete folder");
    }
  };

  const handleUpdateFolder = async (folderId) => {
    if (!editingFolderName.trim()) return;
    try {
      const response = await fetch(`/api/folders/${folderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          name: editingFolderName.trim(),
          projectId: projectId,
          module: "plan" 
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update folder");
      }
      setEditingFolderId(null);
      setEditingFolderName("");
      fetchFolders();
    } catch (err) {
      console.error("Update folder error:", err);
      setError(err.message || "Failed to update folder");
    }
  };

  const renderFolderOption = (folder) => {
    const indent = "  ".repeat(folder.level - 1);
    const levelIndicator = `[L${folder.level}]`;
    return (
      <option key={folder._id} value={folder._id}>
        {indent}{levelIndicator} {folder.name}
      </option>
    );
  };

  const canHaveChildren = (folder) => {
    return folder.level < 3; // 最多3级
  };

  const getAvailableParents = () => {
    return folders.filter(folder => folder.level < 3);
  };

  return (
    <div className="flex h-screen">
      <Sidebar userRole={userRole} projectId={projectId} scenario="plan" />

      <div className="flex-1 p-6 bg-gray-50 text-[12px] ml-[60px]">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-[18px]">Edit Plan Folders</h2>
          <img src="/images/gemterra-logo-20260112-t.png" alt="Gemterra" className="h-6" />
        </div>

        <button
          onClick={() => navigate("/planfile_page")}
          className="bg-gray-300 text-black px-3 py-1 rounded hover:bg-gray-400 mb-4 text-[12px]"
        >
          Back
        </button>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* 添加新文件夹表单 */}
        <form onSubmit={handleAddFolder} className="mb-6 p-4 bg-white rounded shadow">
          <h3 className="text-lg font-medium mb-4">Add New Folder</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Parent Folder (optional - leave empty for Level 1)
            </label>
            <select
              value={selectedParentId}
              onChange={(e) => setSelectedParentId(e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="">Root Level (Level 1)</option>
              {getAvailableParents().map((folder) => renderFolderOption(folder))}
            </select>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="flex-1 p-2 border rounded text-[12px]"
              required
            />
            <button
              type="submit"
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 text-[12px]"
            >
              Add Folder
            </button>
          </div>
        </form>

        {/* 文件夹列表 */}
        <div className="bg-white rounded shadow">
          <h3 className="text-lg font-medium p-4 border-b">Existing Folders (Hierarchical View)</h3>
          <div className="p-4">
            {folders.length === 0 ? (
              <p className="text-gray-500">No folders found.</p>
            ) : (
              <div className="space-y-2">
                {folders.map((folder) => (
                  <div
                    key={folder._id}
                    className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                    style={{ marginLeft: `${(folder.level - 1) * 20}px` }}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        Level {folder.level}
                      </span>
                      {editingFolderId === folder._id ? (
                        <input
                          type="text"
                          value={editingFolderName}
                          onChange={(e) => setEditingFolderName(e.target.value)}
                          className="p-1 border rounded text-[12px]"
                          autoFocus
                        />
                      ) : (
                        <span className="font-medium">{folder.name}</span>
                      )}
                      <span className="text-xs text-gray-500">
                        ({folder.fileCount || 0} files)
                      </span>
                    </div>
                    
                    <div className="flex gap-2">
                      {editingFolderId === folder._id ? (
                        <>
                          <button
                            onClick={() => handleUpdateFolder(folder._id)}
                            className="bg-green-500 text-white px-2 py-1 rounded text-[12px] hover:bg-green-600"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingFolderId(null);
                              setEditingFolderName("");
                            }}
                            className="bg-gray-500 text-white px-2 py-1 rounded text-[12px] hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setEditingFolderId(folder._id);
                              setEditingFolderName(folder.name);
                            }}
                            className="bg-blue-500 text-white px-2 py-1 rounded text-[12px] hover:bg-blue-600"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteFolder(folder._id)}
                            className="bg-red-500 text-white px-2 py-1 rounded text-[12px] hover:bg-red-600"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanEditFolder;
